import { setHiddenFields } from "./utils/utils"
import { protocolClassNames, firstProtocolSectionClassNames, objectClassNames, arrayClassNames } from "../classes"
import { jrctProtocolUISchema } from "./jrctProtocolUISchema"
import { ctisProtocolUISchema } from "./ctisProtocolUISchema"
import { ctgProtocolUISchema } from "./ctgProtocolUISchema"
import { eupasProtocolUISchema } from "./eupasProtocolUISchema"
import { secondaryAttributesUISchema } from "./secondaryAttributesUISchema"

const hideFields = {
  root: [
    "unapproved_new_formulation_marketed_compound",
    "availability_expanded_access",
    "is_clinical_study",
    "vendor",
    "vendor_contact",
    "acquired_from",
    "transferred_to",
    "proprietary_name",
    "established_name",
    "product_manufactured",
    "compound_code",
    "generic_name",
    "brand_name",
    "primary_cro",
    "us_fda_regulated_drug_product",
    "us_fda_regulated_device_product",
    "secondary_ids",
    "general",
    "status",
    // "milestone_dates",
    "protocol_amendments",
    "study_details_detailed_description",
    "sponsoring_division",
    "sponsoring_unit",
    "ctms_study_type",
    "ctms_study_title",
    "ctms_study_status",
    "study_termination_decision_date",
    "study_termination_reason",
    "study_termination_date",
    "study_other",
    "complex_study_program",
    "same_study_design",
    "post_study_access",
    "study_extension",
    "plsrs_posted_on_sponsor_website_off_lslv",
    "plsrs_posted_on_sponsor_website_off_pcd",
    "public_disclosure_synopsis_posted_on_sponsor_website",
    "post_authorization_study_type",
    "disease_area_stronghold",
  ],
  secondary_attributes: {
    root: [
      "usa",
      "internal",
      "germany",
      "spain",
      "netherlands",
      "mexico",
      "south_korea",
      "japan",
      "united_kingdom",
      "australia_and_newzealand",
      "south_africa",
      "switzerland",
      "china",
    ],
    europe: {
      root: [
        "eu_marketing_authorization",
        "ema_cta_date",
        "ema_cta",
        "pass_category",
        "pediatric_study",
        "eu_pip",
        "date_of_latest_approval",
        "part_of_an_agreed_pip",
        "date_of_cta",
        "date_of_results",
        "eu_subsequent_marketing_authorization_application_date",
        "marketing_authorization_holder_for_the_product_under_investigation",
        "pass",
        "eudract_interim_results_required",
        "trial_ongoing_in_eu_eea_on_after_jan312025",
        "sponsor_opted_for_submission_per_eu_directive",
        "cta_submission_per_eu_directive_complete",
        "intermediate_result_analysis_specified_in_protocol",
        "date_of_initial_application_cta_submitted_in_eu_eea",
        "lpo_interim_analysis",
        "applicable_eu_regulation",
        "eu_article_41",
        "eu_article_45",
        "eu_article_46",
        "intermediate_data_analysis_date",
      ],
      lpo_eu_eea: {
        root: ["baseline", "actual"],
      },
    },
  },
}

const globalConfigs = {
  disableValidation: true,
}

const ctgSchema = ctgProtocolUISchema(globalConfigs)
const eupasSchema = eupasProtocolUISchema(globalConfigs)
const ctisSchema = ctisProtocolUISchema(globalConfigs)
const jrctSchema = jrctProtocolUISchema(globalConfigs)
const secondaryAttributesSchema = secondaryAttributesUISchema(globalConfigs)

export const primeProtocolUISchema = {
  "ui:options": {
    classNames: {
      objectContainer: "rjs-full",
      field: "rjs-full",
    },
  },
  protocol: {
    "ui:order": [
      "protocol_id",
      "study_type",
      "type_of_research",
      // "eudract_number",
      "study_identification",
      "contact_information",
      "investigator_information",
      "non_investigator_information",
      "study_timeline",
      "secondary_attributes",
      "milestone_dates",
      "monetary_sponsor",
      "study_oversight",
      "information_on_multicenter_clinical_trials",
      "overview_of_pharmaceuticals",
      "ctis_form",
      "*",
      "ema_hma_additional_information",
    ],
    "ui:options": {
      title: "Study Identification and Information",
      classNames: protocolClassNames,
    },
    protocol_id: ctgSchema.protocol.protocol_id,
    study_type: ctgSchema.protocol.study_type,
    // eudract_number: eupasSchema.protocol.eudract_number,
    type_of_research: jrctSchema.protocol.type_of_research,
    study_identification: {
      "ui:order": [
        "protocol_id",
        "category_of_study",
        "subcategory_of_study",
        "jrct_number",
        "eupas_registry_number",
        "darwin_eu_study",
        "acronym",
        "study_topic",
        "study_topic_other",
        "brief_title",
        "brief_title_translation",
        "public_title_acronym",
        "official_title",
        "official_title_translation",
        "emarwd_official_title_and_acronym",
        "secondary_ids",
        "is_requested_by_regulator",
        "country_regulator_not_listed",
        "is_study_required_by_risk_management_plan",
        "regulatory_procedure_number",
        "*",
      ],
      "ui:options": {
        classNames: firstProtocolSectionClassNames,
      },
      brief_title: ctgSchema.protocol.study_identification.brief_title,
      brief_title_translation: ctisSchema.protocol.study_identification.brief_title_translation,
      acronym: ctgSchema.protocol.study_identification.acronym,
      official_title: ctgSchema.protocol.study_identification.official_title,
      official_title_translation: ctisSchema.protocol.study_identification.official_title_translation,
      secondary_ids: ctgSchema.protocol.study_identification.secondary_ids,
      is_requested_by_regulator: eupasSchema.protocol.study_identification.is_requested_by_regulator,
      country_regulator_not_listed: eupasSchema.protocol.study_identification.country_regulator_not_listed,
      is_study_required_by_risk_management_plan:
        eupasSchema.protocol.study_identification.is_study_required_by_risk_management_plan,
      regulatory_procedure_number: eupasSchema.protocol.study_identification.regulatory_procedure_number,
      other_study_ids: eupasSchema.protocol.study_identification.other_study_ids,
      other_study_identification_numbers: eupasSchema.protocol.study_identification.other_study_identification_numbers,
      category_of_study: jrctSchema.protocol.study_identification.category_of_study,
      jrct_number: jrctSchema.protocol.study_identification.jrct_number,
      subcategory_of_study: jrctSchema.protocol.study_identification.subcategory_of_study,
      public_title_acronym: jrctSchema.protocol.study_identification.public_title_acronym,
      registered_in_jrct_registry: jrctSchema.protocol.study_identification.registered_in_jrct_registry,
      summary_of_changes: jrctSchema.protocol.study_identification.summary_of_changes,
      eupas_registry_number: eupasSchema.protocol.study_identification.eupas_registry_number,
      darwin_eu_study: eupasSchema.protocol.study_identification.darwin_eu_study,
      study_topic: eupasSchema.protocol.study_identification.study_topic,
      study_topic_other: eupasSchema.protocol.study_identification.study_topic_other,
      emarwd_official_title_and_acronym: eupasSchema.protocol.study_identification.emarwd_official_title_and_acronym,
    },
    ctis_form: ctisSchema.protocol.ctis_form,
    secondary_attributes: {
      "ui:options": {
        classNames: {
          fieldContainer: "rjs-full pr-24 pb-24",
        },
      },
      europe: {
        "ui:options": {
          classNames: {
            fieldContainer: "rjs-full",
          },
        },
        lpo_eu_eea: {
          "ui:options": ctisSchema.protocol.secondary_attributes.europe.lpo_eu_eea["ui:options"],
          forecast: ctisSchema.protocol.secondary_attributes.europe.lpo_eu_eea.forecast,
          ...setHiddenFields(hideFields.secondary_attributes.europe.lpo_eu_eea.root),
        },
        ...setHiddenFields(hideFields.secondary_attributes.europe.root),
      },
      ...setHiddenFields(hideFields.secondary_attributes.root),
    },
    mscs: ctisSchema.protocol.mscs,
    contact_information: {
      "ui:order": [
        "sponsor",
        "collaborators",
        "responsible_party",
        "investigator_name",
        "investigator_official_title",
        "investigator_affiliation",
        "investigator_center",
        "encepp_center_organization_affiliation",
        "encepp_center_website",
        "*",
        "institution_conducting_study",
        "institution_conducting_study_if_not_in_list",
        "additional_institutions",
        "additional_institutions_if_not_in_list",
        "networks_registered_encepp",
        "other_networks",
        "centers_registered_encepp",
        "other_centers",
      ],
      "ui:options": {
        title: "Study Sponsor, Collaborators and Contact Information",
        classNames: {
          objectTitleContainer: "rjs-h1-container",
          objectContainer: "rjs-object-container pt-24",
          objectTitle: "rjs-h1",
        },
      },
      sponsor: ctgSchema.protocol.contact_information.sponsor,
      collaborators: ctgSchema.protocol.contact_information.collaborators,
      responsible_party: ctgSchema.protocol.contact_information.responsible_party,
      investigator_name: ctgSchema.protocol.contact_information.investigator_name,
      investigator_official_title: ctgSchema.protocol.contact_information.investigator_official_title,
      investigator_affiliation: ctgSchema.protocol.contact_information.investigator_affiliation,
      central_contacts: ctgSchema.protocol.contact_information.central_contacts,
      central_contacts_backup: ctgSchema.protocol.contact_information.central_contacts_backup,
      overall_study_officials: ctgSchema.protocol.contact_information.overall_study_officials,
      investigator_center: eupasSchema.protocol.contact_information.investigator_center,
      department_research_group: eupasSchema.protocol.contact_information.department_research_group,
      encepp_center_organization_affiliation:
        eupasSchema.protocol.contact_information.encepp_center_organization_affiliation,
      encepp_center_website: eupasSchema.protocol.contact_information.encepp_center_website,
      networks_registered_encepp: eupasSchema.protocol.contact_information.networks_registered_encepp,
      other_networks: eupasSchema.protocol.contact_information.other_networks,
      centers_registered_encepp: eupasSchema.protocol.contact_information.centers_registered_encepp,
      other_centers: eupasSchema.protocol.contact_information.other_centers,
      country_study_is_conducted: eupasSchema.protocol.contact_information.country_study_is_conducted,
      investigator_contact_details: eupasSchema.protocol.contact_information.investigator_contact_details,
      paediatric_investigation_plan:
        ctisSchema.protocol.study_description.scientific_advice_and_pip.paediatric_investigation_plan,
      institution_conducting_study: eupasSchema.protocol.contact_information.institution_conducting_study,
      institution_conducting_study_if_not_in_list:
        eupasSchema.protocol.contact_information.institution_conducting_study_if_not_in_list,
      additional_institutions: eupasSchema.protocol.contact_information.additional_institutions,
      additional_institutions_if_not_in_list:
        eupasSchema.protocol.contact_information.additional_institutions_if_not_in_list,
    },
    study_oversight: {
      "ui:options": {
        title: "Oversight",
        classNames: {
          objectTitleContainer: "rjs-h1-container",
          objectTitle: "rjs-h1",
          objectContainer: "rjs-oc pt-24",
          fieldContainer: "rjs-full",
        },
      },
      us_drug: ctgSchema.protocol.study_oversight.us_drug,
      us_device: ctgSchema.protocol.study_oversight.us_device,
      us_ind: ctgSchema.protocol.study_oversight.us_ind,
      human_subjects_protection_review: ctgSchema.protocol.study_oversight.human_subjects_protection_review,
      data: ctgSchema.protocol.study_oversight.data,
      fda: ctgSchema.protocol.study_oversight.fda,
    },
    study_timeline: {
      ...jrctSchema.protocol.study_timeline,
      "ui:options": {
        title: "Study Timelines and Status",
        classNames: {
          objectTitleContainer: "rjs-h1-container",
          objectTitle: "rjs-h1",
          objectContainer: "pt-24",
          fieldContainer: "m-0 rjs-full",
        },
      },
      record_verification_date: ctgSchema.protocol.study_timeline.record_verification_date,
      recruitment: {
        "ui:options": {
          label: false,
          classNames: {
            fieldContainer: "rjs-full",
          },
        },
        ...jrctSchema.protocol.study_timeline.recruitment,
        recruitment_status: ctgSchema.protocol.study_timeline.recruitment.recruitment_status,
        study_stopped: ctgSchema.protocol.study_timeline.recruitment.study_stopped,
      },
      milestone_dates: {
        "ui:options": {
          title: "Milestone Dates",
          classNames: {
            fieldContainer: "rjs-full",
            objectTitle: "rjs-h2",
          },
        },
        estimated_recruitment_start_date_eea:
          ctisSchema.protocol.study_timeline.milestone_dates.estimated_recruitment_start_date_eea,
      },
      signed_funding_contract_date: eupasSchema.protocol.study_timeline.signed_funding_contract_date,
      // data_collection_start_date: eupasSchema.protocol.study_timeline.data_collection_start_date,
      data_analysis_start_date: eupasSchema.protocol.study_timeline.data_analysis_start_date,
      interim_report_date: eupasSchema.protocol.study_timeline.interim_report_date,
      final_study_report_date: eupasSchema.protocol.study_timeline.final_study_report_date,
      more_details_sources_of_funding: eupasSchema.protocol.study_timeline.more_details_sources_of_funding,
      other_funding_sources: ctisSchema.protocol.study_timeline.other_funding_sources,
      expanded: {
        "ui:options": {
          classNames: arrayClassNames.primary,
        },
        expanded_status: ctgSchema.protocol.study_timeline.expanded.expanded_status,
      },
    },
    milestone_dates: ctgSchema.protocol.milestone_dates,
    study_description: {
      ...jrctSchema.protocol.study_description,
      "ui:order": [
        "brief_summary",
        "brief_summary_translations",
        "detailed_description",
        "secondary_objective",
        "secondary_objective_translations",
        "*",
      ],
      "ui:options": {
        title: "Study Description and Conditions",
        classNames: {
          fieldContainer: "rjs-full",
          objectContainer: "rjs-gap-16 pb-24",
          objectTitleContainer: "rjs-h1-container",
          objectTitle: "rjs-h1",
        },
      },
      trial_scope: ctisSchema.protocol.study_description.trial_scope,
      ctis_trial_phase: ctisSchema.protocol.study_description.ctis_trial_phase,
      brief_summary: ctgSchema.protocol.study_description.brief_summary,
      brief_summary_translations: ctisSchema.protocol.study_description.brief_summary_translations,
      detailed_description: ctgSchema.protocol.study_description.detailed_description,
      secondary_objective: ctisSchema.protocol.study_description.secondary_objective,
      secondary_objective_translations: ctisSchema.protocol.study_description.secondary_objective_translations,
      study_conditions: ctgSchema.protocol.study_description.study_conditions,
      study_keywords: ctgSchema.protocol.study_description.study_keywords,
      low_intervention_trial: ctisSchema.protocol.study_description.low_intervention_trial,
      justification_for_low_intervention_trial:
        ctisSchema.protocol.study_description.justification_for_low_intervention_trial,
      medical_conditions: ctisSchema.protocol.study_description.medical_conditions,
      additional_medical_conditions: eupasSchema.protocol.study_description.additional_medical_conditions,
      therapeutic_area: ctisSchema.protocol.study_description.therapeutic_area,
      meddra: ctisSchema.protocol.study_description.meddra,
      principle_inclusion_criteria: ctisSchema.protocol.study_description.principle_inclusion_criteria,
      principle_exlusion_criteria: ctisSchema.protocol.study_description.principle_exlusion_criteria,
      protocol_information: ctisSchema.protocol.study_description.protocol_information,
      scientific_advice_and_pip: ctisSchema.protocol.study_description.scientific_advice_and_pip,
      associated_clinical_trials: ctisSchema.protocol.study_description.associated_clinical_trials,
      sponsors: ctisSchema.protocol.study_description.sponsors,
      products: ctisSchema.protocol.study_description.products,
    },
    study_scope: {
      ...jrctSchema.protocol.study_scope,
      "ui:order": [
        "study_phase",
        "patient_registry",
        "primary_purpose",
        "patient",
        "follow_up_duration",
        "duration_type",
        "additional_patient_registry_data",
        "observational_study_model",
        "other_non_interventional_study_design",
        "*",
      ],
      "ui:options": {
        title: "Study Scope and Design",
        classNames: {
          objectTitleContainer: "rjs-h1-container",
          objectTitle: "rjs-h1",
          objectContainer: "pb-24",
          fieldContainer: "rjs-full",
        },
      },
      patient_registry: ctgSchema.protocol.study_scope.patient_registry,
      primary_purpose: ctgSchema.protocol.study_scope.primary_purpose,
      study_phase: ctgSchema.protocol.study_scope.study_phase,
      interventional_study_model: ctgSchema.protocol.study_scope.interventional_study_model,
      model_description: ctgSchema.protocol.study_scope.model_description,
      number_of_arms: ctgSchema.protocol.study_scope.number_of_arms,
      masking: ctgSchema.protocol.study_scope.masking,
      masking_description: ctgSchema.protocol.study_scope.masking_description,
      allocation: ctgSchema.protocol.study_scope.allocation,
      enrollment_type: ctgSchema.protocol.study_scope.enrollment_type,
      enrollment_type_obs: ctgSchema.protocol.study_scope.enrollment_type_obs,
      follow_up_duration: ctgSchema.protocol.study_scope.follow_up_duration,
      duration_type: ctgSchema.protocol.study_scope.duration_type,
      additional_patient_registry_data: ctgSchema.protocol.study_scope.additional_patient_registry_data,
      observational_study_model: ctgSchema.protocol.study_scope.observational_study_model,
      other_non_interventional_study_design: eupasSchema.protocol.study_scope.other_non_interventional_study_design,
      time_perspective: ctgSchema.protocol.study_scope.time_perspective,
      biospecimen: ctgSchema.protocol.study_scope.biospecimen,
      enrollment_number: ctgSchema.protocol.study_scope.enrollment_number,
      number_of_cohorts: ctgSchema.protocol.study_scope.number_of_cohorts,
      selection: ctgSchema.protocol.study_scope.selection,
      scope_of_study: eupasSchema.protocol.study_scope.scope_of_study,
      other_scope_description: eupasSchema.protocol.study_scope.other_scope_description,
      design_of_study: eupasSchema.protocol.study_scope.design_of_study,
      countries_outside_eea: ctisSchema.protocol.study_scope.countries_outside_eea,
      clinical_trial_regulatory_scope: eupasSchema.protocol.study_scope.clinical_trial_regulatory_scope,
      clinical_trial_type: eupasSchema.protocol.study_scope.clinical_trial_type,
      data_collection_method: eupasSchema.protocol.study_scope.data_collection_method,
      summary_results: eupasSchema.protocol.study_scope.summary_results,
    },
    study_arms: {
      "ui:options": {
        title: "Arms and Interventions",
        classNames: {
          objectTitleContainer: "rjs-h1-container",
          objectTitle: "rjs-h1",
          objectContainer: "pb-24",
          fieldContainer: "rjs-full",
          field: "rjs-full",
        },
      },
      arms: ctgSchema.protocol.study_arms.arms,
      interventions: ctgSchema.protocol.study_arms.interventions,
      crossref: ctgSchema.protocol.study_arms.crossref,
      // has_drug_information: eupasSchema.protocol.study_arms.has_drug_information,
      // study_drugs: eupasSchema.protocol.study_arms.study_drugs,
      setting: eupasSchema.protocol.study_arms.setting,
      eupas_interventions: {
        ...eupasSchema.protocol.study_arms.eupas_interventions,
        "ui:options": {
          ...eupasSchema.protocol.study_arms.eupas_interventions["ui:options"],
          title: "EMA RWD Interventions",
        },
      },
      comparators: eupasSchema.protocol.study_arms.comparators,
      medicine_name: eupasSchema.protocol.study_arms.medicine_name,
      other_medicine_name: eupasSchema.protocol.study_arms.other_medicine_name,
      substance_inns: eupasSchema.protocol.study_arms.substance_inns,
      atc_code: eupasSchema.protocol.study_arms.atc_code,
    },
    study_eligibilty: {
      ...jrctSchema.protocol.study_eligibilty,
      "ui:order": [
        "*",
        "sources_of_data",
        "registered_data_sources",
        "other_registered_data_sources",
        "other_data_source",
      ],
      "ui:options": {
        title: "Eligibility",
        classNames: {
          objectTitleContainer: "rjs-h1-container",
          objectTitle: "rjs-h1",
          objectContainer: "pb-24",
          fieldContainer: "rjs-full",
        },
      },
      sex: ctgSchema.protocol.study_eligibilty.sex,
      gender_based: ctgSchema.protocol.study_eligibilty.gender_based,
      age_limits: ctgSchema.protocol.study_eligibilty.age_limits,
      accept_healthy_volunteers: ctgSchema.protocol.study_eligibilty.accept_healthy_volunteers,
      is_clinical_study: secondaryAttributesSchema.protocol.study_eligibilty.is_clinical_study,
      eligibility_criteria: ctgSchema.protocol.study_eligibilty.eligibility_criteria,
      study_population_description: ctgSchema.protocol.study_eligibilty.study_population_description,
      sampling_method: ctgSchema.protocol.study_eligibilty.sampling_method,
      population_under_study: {
        "ui:options": {
          title: "Population under study",
          label: false,
          classNames: objectClassNames.tertiary,
        },
        age: eupasSchema.protocol.study_eligibilty.population_under_study.age,
        other_population: eupasSchema.protocol.study_eligibilty.population_under_study.other_population,
        age_range_ctis: ctisSchema.protocol.study_eligibilty.population_under_study.age_range_ctis,
        age_range_secondary_ctis: ctisSchema.protocol.study_eligibilty.population_under_study.age_range_secondary_ctis,
        clinical_trial_group: ctisSchema.protocol.study_eligibilty.population_under_study.clinical_trial_group,
        vulnerable_population: ctisSchema.protocol.study_eligibilty.population_under_study.vulnerable_population,
        recruitment_population_group:
          ctisSchema.protocol.study_eligibilty.population_under_study.recruitment_population_group,
        subjects_incapable_giving_consent:
          ctisSchema.protocol.study_eligibilty.population_under_study.subjects_incapable_giving_consent,
        emergency_situation_description:
          ctisSchema.protocol.study_eligibilty.population_under_study.emergency_situation_description,
        other_description: ctisSchema.protocol.study_eligibilty.population_under_study.other_description,
      },
      special_interest_of_population: eupasSchema.protocol.study_eligibilty.special_interest_of_population,
      other_special_interest_of_population: eupasSchema.protocol.study_eligibilty.other_special_interest_of_population,
      age_groups: eupasSchema.protocol.study_eligibilty.age_groups,
      sources_of_data: eupasSchema.protocol.study_eligibilty.sources_of_data,
      registered_data_sources: eupasSchema.protocol.study_eligibilty.registered_data_sources,
      other_registered_data_sources: eupasSchema.protocol.study_eligibilty.registered_data_sources,
      other_data_source: eupasSchema.protocol.study_eligibilty.other_data_source,
      short_description_of_study_population:
        eupasSchema.protocol.study_eligibilty.short_description_of_study_population,
    },
    study_ipd: {
      ...jrctSchema.protocol.study_ipd,
      "ui:options": {
        title: "IPD Sharing Statements",
        classNames: {
          fieldContainer: "rjs-full",
          objectContainer: "rjs-gap-16 pb-24",
          objectTitleContainer: "rjs-h1-container",
          objectTitle: "rjs-h1",
        },
      },
      plan_to_share_IPD: ctgSchema.protocol.study_ipd.plan_to_share_IPD,
      plan_description: ctgSchema.protocol.study_ipd.plan_description,
      ipd_sharing_info: ctgSchema.protocol.study_ipd.ipd_sharing_info,
      time_frame: ctgSchema.protocol.study_ipd.time_frame,
      access_criteria: ctgSchema.protocol.study_ipd.access_criteria,
      url_text: ctgSchema.protocol.study_ipd.url_text,
    },
    study_outcome_measures: {
      "ui:options": {
        title: "Study Endpoints / Outcome Measures",
        classNames: {
          fieldContainer: "rjs-full",
          objectContainer: "rjs-gap-16 pb-24",
          objectTitleContainer: "rjs-h1-container",
          objectTitle: "rjs-h1",
        },
      },
      outcome_measures: {
        "ui:options": ctgSchema.protocol.study_outcome_measures.outcome_measures["ui:options"],
        items: {
          title: ctgSchema.protocol.study_outcome_measures.outcome_measures.items.title,
          time_frame: ctgSchema.protocol.study_outcome_measures.outcome_measures.items.time_frame,
          description: ctgSchema.protocol.study_outcome_measures.outcome_measures.items.description,
          type: ctgSchema.protocol.study_outcome_measures.outcome_measures.items.type,
        },
      },
      outcome_measures_ctis: {
        "ui:options": {
          ...ctisSchema.protocol.study_outcome_measures.outcome_measures_ctis["ui:options"],
          title: "Outcome Measures - CTIS",
        },
        items: ctisSchema.protocol.study_outcome_measures.outcome_measures_ctis.items,
      },
      eupas_outcomes: {
        ...eupasSchema.protocol.study_outcome_measures.eupas_outcomes,
        "ui:options": {
          ...eupasSchema.protocol.study_outcome_measures.eupas_outcomes["ui:options"],
          title: "EMA RWD Outcomes",
        },
      },
      statistical_analysis_plan: eupasSchema.protocol.study_outcome_measures.statistical_analysis_plan,
    },
    study_reference: {
      "ui:options": {
        title: "Citations, References and URLs",
        classNames: {
          objectTitleContainer: "rjs-h1-container",
          objectTitle: "rjs-h1",
          objectContainer: "pb-24",
        },
      },
      citations: ctgSchema.protocol.study_reference.citations,
      links: ctgSchema.protocol.study_reference.links,
      available: ctgSchema.protocol.study_reference.available,
      is_requesting_encepp_seal: eupasSchema.protocol.study_reference.is_requesting_encepp_seal,
      // make_full_protocol_public_when_study_ends:
      //   eupasSchema.protocol.study_reference.make_full_protocol_public_when_study_ends,
      study_publications: eupasSchema.protocol.study_reference.study_publications,
      encepp_code_of_conduct: eupasSchema.protocol.study_reference.encepp_code_of_conduct,
    },
    investigator_information: jrctSchema.protocol.investigator_information,
    non_investigator_information: jrctSchema.protocol.non_investigator_information,
    information_on_multicenter_clinical_trials: jrctSchema.protocol.information_on_multicenter_clinical_trials,
    overview_of_pharmaceuticals: jrctSchema.protocol.overview_of_pharmaceuticals,
    monetary_sponsor: jrctSchema.protocol.monetary_sponsor,
    irb_information: jrctSchema.protocol.irb_information,
    ema_hma_additional_information: eupasSchema.protocol.ema_hma_additional_information,
    ...setHiddenFields(hideFields.root),
  },
}
